import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Badge } from "./ui/badge";
import { Pagination } from "./ui/pagination";
import { FileText, Plus, Search, Edit, Trash2, Eye, Calendar, Building, DollarSign, Loader2, RefreshCw, Truck } from "lucide-react";
import { toast } from "sonner";
import { challanApiService, ChallanMaster, ChallanStatus } from "../services/challanApi";
import { PermissionGate } from "./ui/PermissionGate";
import { useAuth } from "../contexts/AuthContext";
import { useBranch } from "../contexts/BranchContext";

export function ChallanListingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  
  // API state
  const [challans, setChallans] = useState<ChallanMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Server-side pagination state
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [serverCurrentPage, setServerCurrentPage] = useState(1);

  // Refs to prevent duplicate API calls
  const isFetchingRef = useRef(false);
  const lastBranchIdRef = useRef<number | null>(null);
  const initialMountRef = useRef(true);

  // Challan management route (from backend permissions)
  const CHALLAN_ROUTE = '/challans';

  // Fetch challans from API with server-side pagination
  const fetchChallans = useCallback(async (page: number = currentPage, search: string = debouncedSearchTerm) => {
    // Prevent duplicate concurrent calls
    if (isFetchingRef.current) {
      return;
    }

    // Resolve active branch id: selected branch from header overrides default
    const activeBranchId = selectedBranch || user?.defaultBranchId;

    // Validate branch ID
    if (!activeBranchId) {
      setError('User branch information is required. Please contact administrator.');
      setChallans([]);
      return;
    }

    // Don't fetch if we're already fetching for the same branchId, page, and search
    if (lastBranchIdRef.current === activeBranchId && !initialMountRef.current) {
      // Only skip if nothing else changed - let page and search changes through
      // This check will be handled by the useEffect dependencies
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      lastBranchIdRef.current = activeBranchId;
      
      let response;
      
      // Use search API if there's a search term, otherwise use regular API
      if (search.trim()) {
        response = await challanApiService.searchChallans({
          q: search.trim(),
          branchId: activeBranchId
        });
      } else {
        response = await challanApiService.getChallans({
          branchId: activeBranchId,
          page: page,
          pageSize: pageSize
        });
      }
      
      // Handle the nested pagination structure from the API
      const challanList = response.data || [];
      const pagination = response.pagination || {};
      const totalCount = pagination.totalCount || challanList.length;
      const totalPages = pagination.totalPages || Math.ceil(totalCount / pageSize);
      const currentPage = pagination.currentPage || page;
      
      setChallans(challanList);
      setTotalCount(totalCount);
      setTotalPages(totalPages);
      setServerCurrentPage(currentPage);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch challans';
      setError(errorMessage);
      toast.error(errorMessage);
      setChallans([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
      initialMountRef.current = false;
    }
  }, [user?.defaultBranchId, selectedBranch, pageSize]);

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchChallans(currentPage, debouncedSearchTerm);
    setRefreshing(false);
  };

  // Debounce search term for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Single effect to fetch data on branch/page/search changes
  // Wait for user to be loaded before making any API calls
  useEffect(() => {
    // Don't fetch until user is loaded
    if (!user) {
      return;
    }

    const activeBranchId = selectedBranch || user?.defaultBranchId;
    
    // Don't fetch if we don't have a branch ID yet
    if (!activeBranchId) {
      return;
    }

    // Don't fetch if we're already fetching for the same branch
    if (isFetchingRef.current && lastBranchIdRef.current === activeBranchId) {
      return;
    }

    // Reset page to 1 when branch changes
    if (lastBranchIdRef.current !== null && lastBranchIdRef.current !== activeBranchId) {
      setCurrentPage(1);
    }

    fetchChallans(1, debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, user?.defaultBranchId, debouncedSearchTerm, user]);

  // Handle page changes separately (when branch/search hasn't changed)
  useEffect(() => {
    if (!user) return;
    
    const activeBranchId = selectedBranch || user?.defaultBranchId;
    if (!activeBranchId) return;
    
    // Skip if this is the initial mount (handled by the effect above)
    if (initialMountRef.current) return;
    
    // Only fetch if page changed but branch/search didn't change
    if (lastBranchIdRef.current === activeBranchId && !isFetchingRef.current) {
      fetchChallans(currentPage, debouncedSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // With server-side pagination, we use the data directly from the server
  const paginatedChallans = challans;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleNewChallan = () => {
    navigate('/challans/new');
  };

  const handleEditChallan = (challan: ChallanMaster) => {
    navigate(`/challans/${challan.id}/edit`);
  };

  const handleViewChallan = (challan: ChallanMaster) => {
    navigate(`/challans/${challan.id}/view`);
  };

  const handleDeleteChallan = async (challan: ChallanMaster) => {
    if (!challan.id) {
      toast.error('Invalid challan ID');
      return;
    }

    try {
      await challanApiService.deleteChallan(challan.id.toString());
      toast.success(`Challan ${challan.challanNo} deleted successfully!`);
      // Refresh the list after deletion
      await fetchChallans();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete challan';
      toast.error(errorMessage);
    }
  };

  const getStatusColor = useCallback((status: ChallanStatus) => {
    switch (status) {
      case ChallanStatus.IN_TRANSIT: return 'bg-blue-100 text-blue-700';
      case ChallanStatus.DELIVERED: return 'bg-green-100 text-green-700';
      case ChallanStatus.CANCELLED: return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }, []);

  // Format date helper - memoized for performance
  const formatDate = useCallback((dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid Date';
    }
  }, []);

  // Format currency helper - memoized for performance
  const formatCurrency = useCallback((amount: number) => {
    if (amount === null || amount === undefined) return '₹0';
    return `₹${amount.toLocaleString()}`;
  }, []);

  // Show error if user branch information is missing
  if (!user?.defaultBranchId && !loading) {
    return (
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6 text-center">
            <div className="text-yellow-600 mb-4">
              <FileText className="h-12 w-12 mx-auto mb-3" />
              <p className="text-lg font-semibold">Branch Information Required</p>
              <p className="text-sm text-gray-600 mt-2">
                Your account needs to be associated with a branch to view challans. 
                Please contact your administrator to set up your branch information.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !loading) {
    return (
      <div className="space-y-6">
        <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
          <CardContent className="p-6 text-center">
            <div className="text-red-600 mb-4">
              <FileText className="h-12 w-12 mx-auto mb-3" />
              <p className="text-lg font-semibold">Error Loading Challans</p>
              <p className="text-sm text-gray-600 mt-2">{error}</p>
            </div>
            <Button onClick={handleRefresh} className="bg-primary-600 hover:bg-primary-700">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-primary-200 flex items-center justify-center">
                <FileText className="h-5 w-5 text-primary-700" />
              </div>
              <div>
                <CardTitle className="text-xl text-gray-900">Challan Details</CardTitle>
                <p className="text-gray-600 text-sm">Manage your challans and deliveries</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                disabled={refreshing}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                {refreshing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
              <PermissionGate route={CHALLAN_ROUTE} action="create">
                <Button
                  onClick={handleNewChallan}
                  className="bg-primary-500 text-gray-700 hover:bg-gray-50 shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Challan
                </Button>
              </PermissionGate>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search challans..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="text-sm text-gray-600">
              {paginatedChallans.length} of {totalCount} challans
            </div>
          </div>

          {/* Challans Table */}
          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
                <p className="text-gray-600">Loading challans...</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                    <TableHead>Challan No</TableHead>
                    <TableHead>From Branch</TableHead>
                    <TableHead>To Branch</TableHead>
                    <TableHead>Date of Supply</TableHead>
                    <TableHead>Vehicle No</TableHead>
                    <TableHead>Total Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedChallans.map((challan) => (
                    <TableRow key={challan.id} className="hover:bg-primary-50/50">
                      <TableCell className="font-medium">{challan.challanNo || 'N/A'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building className="h-3 w-3 text-gray-400" />
                          <span>{challan.fromBranch?.branchCode || `Branch ${challan.fromBranchId}`}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building className="h-3 w-3 text-gray-400" />
                          <span>{challan.toBranch?.branchCode || `Branch ${challan.toBranchId}`}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          <span>{formatDate(challan.datetimeOfSupply || challan.createdOn)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{challan.vehNo || 'N/A'}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-3 w-3 text-gray-400" />
                          <span className="font-medium">{formatCurrency(challan.grandTotal || challan.totalAmount)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(challan.status)} border-0`}>
                          {challan.status || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 justify-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewChallan(challan)}
                            className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                            title="View Challan"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <PermissionGate route={CHALLAN_ROUTE} action="edit">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditChallan(challan)}
                              className="border-primary-200 hover:bg-primary-50 hover:border-primary-300 text-primary-600"
                              title="Edit Challan"
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={CHALLAN_ROUTE} action="delete">
                            {challan.status !== ChallanStatus.DELIVERED && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDeleteChallan(challan)}
                                className="border-red-200 hover:bg-red-50 hover:border-red-300 text-red-600"
                                title="Delete Challan"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {!loading && paginatedChallans.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Truck className="h-12 w-12 mx-auto mb-3 text-primary-300" />
              <p>No challans found matching your search criteria.</p>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

